import { Spine } from "@esotericsoftware/spine-pixi-v7";
import * as PIXI from "pixi.js";
import { Settings } from "../../../settings";

// Configuration constants (export for easy tweaking)
export const WIN_LINES_CONFIG = {
  widthMultiplier: 0.235,
  heightMultiplier: 0.25,
  xOffsetMultiplier: -0.0875,
  yOffsetMultiplier: 0.45,
};

/**
 * WinLinesReelController - Manages win line spine animations
 *
 * Key Features:
 * - Single spine instance covering entire reelset container
 * - Automatic container dimension calculation from children
 * - Responsive scaling and positioning
 * - Win line animation management by line ID
 */
class WinLinesController {
  constructor(container, symbolWidth, symbolHeight, dividerDimensions) {
    this.container = container;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.dividerDimensions = dividerDimensions;

    // Multiple spine instances - one per win line (0-39)
    this.winLineSpines = {}; // { lineId: spineInstance }
    this.spineContainer = new PIXI.Container(); // Container for all spine instances
    this.currentLineId = null;
    this.initialized = false;

    // Animation cache for performance
    this.animationCache = new Map();

    // Add spine container to main container
    this.container.addChild(this.spineContainer);

    // Defer spine creation to avoid blocking main thread
    requestAnimationFrame(() => {
      this.initializeSpines();
    });
  }

  /**
   * Initialize multiple spine instances - one per win line
   */
  initializeSpines() {
    if (!this.symbolWidth || !this.symbolHeight) {
      return;
    }

    // Create 40 spine instances (win_line_0 through win_line_39)
    for (let lineId = 0; lineId < 40; lineId++) {
      const spine = this.createWinLineSpine();

      if (!spine || !spine.skeleton || !spine.state) {
        continue;
      }

      // Add to spine container
      this.spineContainer.addChild(spine);

      // Set initial properties - hidden
      spine.alpha = 0;
      spine.visible = false;

      // Set the specific animation for this line and pause it at frame 0
      const animationName = `win_line_${lineId}`;
      const animations = this.getAvailableAnimations(spine);

      if (animations.includes(animationName)) {
        spine.state.setAnimation(0, animationName, true);
        spine.state.timeScale = 0; // Pause at frame 0
        this.animationCache.set(animationName, true);
      } else {
        this.animationCache.set(animationName, false);
      }

      // Store spine instance
      this.winLineSpines[lineId] = spine;
    }

    // Scale and position the entire spine container
    this.scaleAndPositionSpineContainer();

    this.initialized = true;
  }

  /**
   * Get available animations from a spine instance
   */
  getAvailableAnimations(spine) {
    const targetSpine = spine || Object.values(this.winLineSpines)[0];
    if (!targetSpine?.skeleton?.data?.animations) return [];
    return targetSpine.skeleton.data.animations.map((anim) => anim.name);
  }

  /**
   * Create the win lines spine instance
   */
  createWinLineSpine() {
    try {
      // Check if assets are loaded
      const assets = PIXI.Assets.cache;
      const skeletonAlias = "win_lines-skeleton";
      const atlasAlias = "win_lines-atlas";

      if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) {
        return null;
      }

      const spine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
      });

      // Ensure Spine can handle multiple simultaneous tracks
      if (spine && spine.state) {
        // Pre-allocate tracks to avoid dynamic allocation issues
        spine.state.tracks = new Array(20);
      }

      // Verify the spine was created properly
      if (!spine || !spine.skeleton || !spine.state) {
        return null;
      }

      // Verify skeleton has required data
      if (!spine.skeleton.data || !spine.skeleton.data.animations) {
        return null;
      }

      return spine;
    } catch (error) {
      return null;
    }
  }

  /**
   * Scale and position the spine container using Settings native dimensions
   * This ensures proper centering and scaling within the container bounds
   */
  scaleAndPositionSpineContainer() {
    if (!this.spineContainer || !this.symbolWidth || !this.symbolHeight) {
      return;
    }

    // Use Settings native dimensions
    const nativeSpineWidth = Settings.SYMBOL_WIDTH;
    const nativeSpineHeight = Settings.SYMBOL_HEIGHT;

    // Calculate target frame dimensions based on reel configuration
    const cfg = WIN_LINES_CONFIG;
    const targetFrameWidth =
      this.symbolWidth * Settings.REEL_COUNT * cfg.widthMultiplier;
    const targetFrameHeight =
      this.symbolHeight * Settings.SYMBOLS_VISIBLE * cfg.heightMultiplier;

    // Calculate scale to fit spine within target frame
    const scaleX = targetFrameWidth / nativeSpineWidth;
    const scaleY = targetFrameHeight / nativeSpineHeight;

    // Use uniform scale to maintain aspect ratio
    const scale = Math.min(scaleX, scaleY);

    // Apply scale to entire container (all spines inherit this)
    this.spineContainer.scale.set(scale, scale);

    // Calculate final scaled dimensions
    const scaledWidth = nativeSpineWidth * scale;
    const scaledHeight = nativeSpineHeight * scale;

    // Center the container within the target frame
    const centerX = (targetFrameWidth - scaledWidth) / 2;
    const centerY = (targetFrameHeight - scaledHeight) / 2;

    // Apply position offsets for fine-tuning
    const xOffset = targetFrameWidth * cfg.xOffsetMultiplier;
    const yOffset = targetFrameHeight * cfg.yOffsetMultiplier;

    // Position container with centering and offsets
    this.spineContainer.position.set(centerX + xOffset, centerY + yOffset);
  }

  /**
   * Show multiple win lines simultaneously using individual spine instances
   * @param {Array<number|string>} lineIds - Array of line IDs to show
   */
  showMultipleWinLines(lineIds) {
    if (!this.initialized) {
      return;
    }

    if (!Array.isArray(lineIds) || lineIds.length === 0) {
      return;
    }

    // Hide all spines first
    Object.values(this.winLineSpines).forEach(spine => {
      spine.alpha = 0;
      spine.visible = false;
      spine.state.timeScale = 0;
    });

    // Show and animate only the requested lines
    lineIds.forEach((lineId) => {
      const spine = this.winLineSpines[lineId];
      if (spine) {
        spine.visible = true;
        spine.alpha = 1;
        spine.state.timeScale = 1; // Resume animation
      }
    });

    this.currentLineId = "multiple";
  }

  /**
   * Show win line animation by line ID (for cycling through individual lines)
   * @param {number|string} lineId - The win line ID (0-39)
   */
  showWinLine(lineId) {
    if (!this.initialized) {
      return;
    }

    // Prevent duplicate calls for the same line
    if (this.currentLineId === lineId) {
      return;
    }

    const animationName = `win_line_${lineId}`;
    const hasAnimation = this.animationCache.get(animationName);

    if (!hasAnimation) {
      return;
    }

    // Hide all other spines
    Object.entries(this.winLineSpines).forEach(([id, spine]) => {
      if (id !== String(lineId)) {
        spine.alpha = 0;
        spine.visible = false;
        spine.state.timeScale = 0;
      }
    });

    // Show only the requested line
    const spine = this.winLineSpines[lineId];
    if (spine) {
      spine.visible = true;
      spine.alpha = 1;
      spine.state.timeScale = 1; // Resume animation
    }

    this.currentLineId = lineId;
  }

  /**
   * Hide current win line animation
   */
  hideWinLine() {
    // Hide all spines
    Object.values(this.winLineSpines).forEach(spine => {
      spine.alpha = 0;
      spine.visible = false;
      spine.state.timeScale = 0;
    });

    this.currentLineId = null;
  }

  /**
   * Update spine dimensions and rescale for resize events
   * @param {number} symbolWidth - Updated effective symbol width
   * @param {number} symbolHeight - Updated effective symbol height
   * @param {object} dividerDimensions - Updated divider dimensions (not used but kept for API compatibility)
   */
  updateDimensionsAndRescale(symbolWidth, symbolHeight, dividerDimensions) {
    // Update stored dimensions
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.dividerDimensions = dividerDimensions;

    // Reapply scaling and positioning with new dimensions
    if (this.spineContainer && this.initialized) {
      this.scaleAndPositionSpineContainer();
    }
  }

  /**
   * Handle container resize - reposition spine container with current dimensions
   */
  resizeToContainer() {
    if (this.spineContainer && this.initialized) {
      this.scaleAndPositionSpineContainer();
    }
  }

  /**
   * Retry initialization if it failed initially
   */
  retryInitialization() {
    if (Object.keys(this.winLineSpines).length === 0) {
      this.initializeSpines();
    }
  }

  /**
   * Check if controller is ready (has spine instance)
   */
  isReady() {
    return this.winLineSpine !== null && this.initialized;
  }

  /**
   * Clean up spine instance and resources
   */
  cleanup() {
    if (this.winLineSpine) {
      if (this.winLineSpine.state) {
        this.winLineSpine.state.clearTracks();
        this.winLineSpine.state.clearListeners();
      }
      if (this.winLineSpine.parent) {
        this.winLineSpine.parent.removeChild(this.winLineSpine);
      }
      this.winLineSpine.destroy({
        children: true,
        texture: false,
        baseTexture: false,
      });
      this.winLineSpine = null;
    }

    // Clear caches
    if (this.animationCache) {
      this.animationCache.clear();
    }

    this.currentLineId = null;
    this.initialized = false;
  }
}

export default WinLinesController;
